"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
const SettingsModal = dynamic(() => import("../SettingsModal"), { ssr: false });
const LanguageDropdown = dynamic(() => import("../common/LanguageDropdown"), { ssr: false });
const SearchBar = dynamic(() => import("./homepage/SearchBar"), { ssr: false });
import Image from "next/image";
import { useHeader } from "@/hooks/customer/useHeader";
import {
  goToSupplierDashboard,
  useSupplierSessionHint,
} from "@/hooks/useSupplierSessionHint";
import Breadcrumb from "../Breadcrumb";

const HeaderDesktop = ({ heroRef }) => {
  const {
    router,
    isAuthenticated,
    isSettingsOpen,
    setIsSettingsOpen,
    profileRef,
    showSearch,
    profileSummary,
    profileLoading,
    displayName,
    initials,
    avatarUrl,
    headerPosition,
  } = useHeader(heroRef);
  const { hasSupplierSession } = useSupplierSessionHint();
  return (
    <>
      <header className={`${headerPosition} z-100 pt-4 lg:pt-6 pb-2`}>
        <div className="ta-desktop-home-container">
          <div className="bg-white/95 backdrop-blur-md border border-[#D1D1D1] rounded-2xl shadow-sm">
            <div className="flex items-center justify-between min-h-16 h-16 px-3 xl:px-5 gap-2 xl:gap-4">
              {/* logo */}
              <Link href="/" className="flex shrink-0 items-center gap-2">
                <div className="relative h-8 xl:h-12 w-auto aspect-3/1">
                  <Image
                    src="/images/logo1.png"
                    alt="TripsAdora Logo"
                    fill
                    sizes="(max-width: 1024px) 170px, 200px"
                    className="object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              </Link>

              {/* Search (scroll ke baad) */}
              {showSearch && (
                <div className="w-[200px] lg:w-[250px] xl:w-[300px] transition-all duration-500">
                  <SearchBar animated={true} py={2.5} />
                </div>
              )}

              {/* navigation links */}
              <nav
                className={`hidden md:flex items-center space-x-2 xl:space-x-4 transition-all duration-500`}
              >
                <Link
                  href="/blog"
                  className="text-black link-text font-normal whitespace-nowrap"
                >
                  Travel Blogs
                </Link>
                <Link
                  href="/my-reviews"
                  className="text-black link-text font-normal whitespace-nowrap"
                >
                  Reviews
                </Link>
                <Link
                  href="/rewards"
                  className="text-black link-text font-normal whitespace-nowrap"
                >
                  Rewards
                </Link>
              </nav>

              <div className="hidden md:flex items-center space-x-1 xl:space-x-3 relative">
                <LanguageDropdown />

                {isAuthenticated ? (
                  <>
                    {hasSupplierSession && (
                      <button
                        type="button"
                        className="px-1 xl:px-2 py-1.5 btn-text font-medium text-white bg-primary hover:opacity-90 transition-colors rounded-lg cursor-pointer"
                        onClick={() => goToSupplierDashboard(router)}
                        aria-label="Supplier Dashboard"
                      >
                        Supplier Dashboard
                      </button>
                    )}
                    <button
                      type="button"
                      ref={profileRef}
                      className="flex items-center gap-2 hover:opacity-85 transition cursor-pointer"
                      onClick={() => setIsSettingsOpen(true)}
                      aria-label="Open profile menu"
                    >
                      {displayName && (
                        <span
                          className={`hidden sm:block span-text font-medium max-w-[120px] truncate text-black`}
                        >
                          {displayName}
                        </span>
                      )}
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center overflow-hidden bg-primary text-white text-sm font-semibold shadow-lg transition-opacity duration-300 ${
                          profileLoading ? "opacity-70" : "opacity-100"
                        }`}
                      >
                        {avatarUrl ? (
                          <Image
                            src={avatarUrl}
                            alt={displayName || "Profile avatar"}
                            width={36}
                            height={36}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span
                            className="span-text font-medium tracking-wide"
                            title={displayName || "Profile"}
                          >
                            {initials}
                          </span>
                        )}
                      </div>
                    </button>
                  </>
                ) : (
                  <>
                    {hasSupplierSession ? (
                      <button
                        type="button"
                        className="px-1 xl:px-2 py-1.5 btn-text font-medium text-white bg-primary hover:opacity-90 transition-colors rounded-lg cursor-pointer"
                        onClick={() => goToSupplierDashboard(router)}
                        aria-label="Supplier Dashboard"
                      >
                        Supplier Dashboard
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="px-1 xl:px-2 py-1.5 btn-text font-medium text-primary hover:text-primary/80 transition-colors border border-primary rounded-lg cursor-pointer whitespace-nowrap"
                        onClick={() => router.push("/become-supplier")}
                        aria-label="Become Supplier"
                      >
                        Become Supplier
                      </button>
                    )}
                    <button
                      type="button"
                      ref={profileRef}
                      className="px-1 xl:px-2 py-1.5 btn-text font-medium rounded-lg bg-secondary text-white transition-colors hover:bg-secondary hover:text-primary cursor-pointer"
                      onClick={() => router.push("/login")}
                      aria-label="Login"
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      className="px-1 xl:px-2 py-1.5 btn-text font-medium rounded-lg border border-primary text-primary transition-colors hover:bg-primary hover:text-white cursor-pointer whitespace-nowrap"
                      onClick={() => router.push("/sign-up")}
                      aria-label="Sign up"
                    >
                      Sign up
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        profileRef={profileRef}
        profile={profileSummary}
      />
    </>
  );
};

export default HeaderDesktop;
